import React, { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Dialog,
  Box,
  Fade,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { keyframes } from "@mui/system";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const progressMessages = [
  "Gathering your data...",
  "Processing records...",
  "Building your file...",
  "Optimizing for download...",
  "Almost ready...",
  "Finalizing...",
];

export default function FileDownloadLoader({ 
  open = true,
  totalRecords = null,
  fileName = "export.csv"
}) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [recordsProcessed, setRecordsProcessed] = useState(0);

  // Cycle through messages
  useEffect(() => {
    if (!open) return;

    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % progressMessages.length);
    }, 3500);

    return () => clearInterval(messageInterval);
  }, [open]);

  // Simulate progress
  useEffect(() => {
    if (!open) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return prev;
        const increment = Math.random() * 4 + 1;
        return Math.min(prev + increment, 95);
      });
    }, 600);

    return () => clearInterval(progressInterval);
  }, [open]);

  // Simulate records processing
  useEffect(() => {
    if (!open || !totalRecords) return;

    const recordInterval = setInterval(() => {
      setRecordsProcessed((prev) => {
        const increment = Math.floor(Math.random() * 50) + 20;
        return Math.min(prev + increment, totalRecords);
      });
    }, 400);

    return () => clearInterval(recordInterval);
  }, [open, totalRecords]);

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: { xs: "85%", sm: 380 },
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Card elevation={0}>
        <CardContent sx={{ p: 5 }}>
          <Stack spacing={3.5} alignItems="center">
            {/* Floating Icon */}
            <Box
              sx={{
                animation: `${float} 3s ease-in-out infinite`,
              }}
            >
              <FileDownloadOutlinedIcon
                sx={{
                  fontSize: 56,
                  color: "primary.main",
                  opacity: 0.9,
                }}
              />
            </Box>

            {/* Title */}
            <Stack spacing={0.5} alignItems="center">
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Preparing Download
              </Typography>
              <Typography variant="body2" color="text.secondary" fontSize={13}>
                {fileName}
              </Typography>
            </Stack>

            {/* Progress Bar */}
            <Box sx={{ width: "100%" }}>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "rgba(0,0,0,0.06)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    backgroundColor: "primary.main",
                  },
                }}
              />
            </Box>

            {/* Dynamic Message */}
            <Box sx={{ minHeight: 40, textAlign: "center" }}>
              <Fade in key={messageIndex} timeout={600}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {progressMessages[messageIndex]}
                </Typography>
              </Fade>
            </Box>

            {/* Records Counter (if provided) */}
            {totalRecords && (
              <Box
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {recordsProcessed.toLocaleString()} / {totalRecords.toLocaleString()} records
                </Typography>
              </Box>
            )}

            {/* Footer Note */}
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
              This may take a few moments for large datasets
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Dialog>
  );
}